import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { Alert, LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection } from '../components/work-page.tsx';
import { SwapApprovalCard } from '../components/shift-swaps.tsx';
import type { SwapItem, SwapReview } from '@shared/shift-swaps.ts';

type ApproveLink = { id: string; token: string };

interface LinkView {
  item: SwapItem;
  review: SwapReview | null;
  alreadyDecided: boolean;
}

/**
 * Where the emailed approve link lands. Opening it changes nothing; the
 * signed-in approver presses Approve, which spends the link. A second click —
 * on this page or the email — finds the swap already decided.
 */
export default function SwapApprovalPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [view, setView] = useState<(LinkView & { link: ApproveLink }) | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'decided' | 'approved' | 'failed'>('loading');
  const [busy, setBusy] = useState(false);
  // Why the last Approve was refused. Kept while the review is read again: the
  // review cannot show every reason (an own-calendar clash, for one).
  const [error, setError] = useState('');
  // One object per link shown. Every answer is applied only while its own link
  // is still the current one, so a slow read for an earlier link can never
  // replace this page, and Approve always spends the link that is on screen.
  const current = useRef<ApproveLink | null>(null);
  const live = (link: ApproveLink) => current.current === link;

  useEffect(() => {
    const link = { id, token };
    current.current = link;
    setView(null);
    setState('loading');
    setBusy(false);
    setError('');
    void load(link);
    return () => {
      if (current.current === link) current.current = null;
    };
  }, [id, token]);

  async function load(link: ApproveLink) {
    try {
      const result = await api.get<LinkView>(
        `/swaps/${encodeURIComponent(link.id)}/email-link?token=${encodeURIComponent(link.token)}`,
      );
      if (!live(link)) return;
      setView({ ...result, link });
      setState(result.alreadyDecided ? 'decided' : 'ready');
    } catch (caught) {
      if (!live(link)) return;
      const status = (caught as { status?: number }).status;
      setState(status && status < 500 ? 'invalid' : 'failed');
    }
  }

  async function approve() {
    const link = view?.link;
    if (busy || !link || !live(link)) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/swaps/${encodeURIComponent(link.id)}/email-approve`, { token: link.token });
      if (live(link)) setState('approved');
    } catch (caught) {
      if (!live(link)) return;
      const code = (caught as { code?: string }).code;
      if (code === 'SWAP_ALREADY_DECIDED') setState('decided');
      else {
        setError((caught as { message?: string }).message || t('shiftSwaps.actionFailed'));
        await load(link);
      }
    } finally {
      if (live(link)) setBusy(false);
    }
  }

  return (
    <WorkPage title={t('shiftSwaps.email.title')}>
      <nav aria-label={t('shiftSwaps.title')}>
        <Link to="/staff/swaps" className="text-[color:var(--color-brand-strong)] underline underline-offset-4">
          {t('shiftSwaps.email.back')}
        </Link>
      </nav>
      <WorkSection title={t('shiftSwaps.review.title')}>
        {state === 'loading' ? <LoadingBlock label={t('shiftSwaps.loading')} /> : null}
        {state === 'invalid' ? <Alert tone="error">{t('shiftSwaps.email.invalid')}</Alert> : null}
        {state === 'failed' ? <Alert tone="error">{t('shiftSwaps.readFailed')}</Alert> : null}
        {state === 'decided' ? <Alert tone="info">{t('shiftSwaps.email.alreadyDecided')}</Alert> : null}
        {state === 'approved' ? <Alert tone="success">{t('shiftSwaps.email.approved')}</Alert> : null}
        {error && state !== 'approved' ? <Alert tone="error">{error}</Alert> : null}
        {view && (state === 'ready' || state === 'decided') ? (
          <SwapApprovalCard
            item={view.item}
            review={view.review}
            busy={busy}
            allowDecline={false}
            onDecide={state === 'ready' ? () => void approve() : undefined}
          />
        ) : null}
      </WorkSection>
    </WorkPage>
  );
}
